import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const checkStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(checkStandalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show prompt if not standalone, not recently dismissed
    if (!checkStandalone && daysSinceDismissed > 7) {
      // For iOS, show after a short delay
      if (iOS) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      if (!checkStandalone && daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="pwa-prompt-overlay">
      <div className="pwa-prompt">
        <button className="pwa-prompt-close" onClick={handleDismiss}>
          <X size={20} />
        </button>
        
        <div className="pwa-prompt-icon">
          <Smartphone size={32} />
        </div>
        
        <h3>Uygulamayı Yükle</h3>
        
        {isIOS ? (
          <div className="pwa-ios-instructions">
            <p>Bu siteyi ana ekranınıza ekleyin:</p>
            <ol>
              <li>
                <span className="ios-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16,6 12,2 8,6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>
                Paylaş butonuna tıklayın
              </li>
              <li>
                <span className="ios-icon">➕</span>
                "Ana Ekrana Ekle" seçin
              </li>
            </ol>
          </div>
        ) : (
          <>
            <p>
              Hızlı erişim için uygulamayı telefonunuza yükleyin.
              İnternet olmadan da temel bilgilere ulaşın!
            </p>
            <div className="pwa-prompt-actions">
              <button className="pwa-install-btn" onClick={handleInstall}>
                <Download size={18} />
                Yükle
              </button>
              <button className="pwa-later-btn" onClick={handleDismiss}>
                Daha Sonra
              </button>
            </div>
          </>
        )}
        
        <div className="pwa-features">
          <span>✓ Hızlı açılır</span>
          <span>✓ Çevrimdışı destek</span>
          <span>✓ Bildirimler</span>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
